// Read models over the single order array: what each ops screen receives.

import type { DeliveryDetails, DeliveryEvidenceFile, DeliveryEventEnriched } from '../../types/delivery.type';
import type { FacilityIncomingOrder } from '../../types/facility.type';
import type {
  DriverRequestStatus,
  FleetDriverRequestMonitor,
  LinkedPickupMonitor,
  RecyclerOrderOverview,
  RecyclerPreOrderMonitor,
} from '../../types/monitoring.type';
import type { ApprovalOrderRow } from '../../types/order-approval.type';
import { PickupOrderAmountUnit, type OrderSchema } from '../../types/order.type';
import type { OpsRouteRun, OpsRouteRunStop } from '../../types/route-run.type';
import type { DriverHistoricalOrder } from '../../types/transportation.type';
import {
  DEMO_APPETITE_LINES,
  DEMO_DRIVERS,
  DEMO_FACILITIES,
  DEMO_MATERIAL_IDS,
  DEMO_RECYCLER_ORG_ID,
  DEMO_SITES,
  DEMO_TRUCKS,
  DAY_MS,
  HOUR_MS,
  categoryByCode,
  hashString,
  ids,
  iso,
  materialById,
  monthStart,
  demoId,
} from '../core';
import { materialName, toMaterialType, wasteTypeOf } from './catalog';
import { mapsUrl, placeholderPhoto, round1, signatureImage, tr, unit } from './common';
import { listTrucks, truckName, truckRecordById } from './fleet';
import {
  facilityDisplayName,
  facilityRecordById,
  pointDisplayName,
  pointRecordById,
  routeDisplayName,
  routeRecordById,
  supplierDisplayName,
} from './network';
import { allOrders, allRuns, driverRecord, orderDay, priceLoad, type OpsOrder, type OpsRun } from './orders';
import { organizationName } from './people';
import { siteAddress, siteDisplayName } from './sites';

export function driverName(id: string | null | undefined): string | null {
  const d = driverRecord(id);
  return d ? tr(d.full_name, d.full_name_ar) : null;
}

function pointName(o: OpsOrder): string | null {
  return pointDisplayName(o.collection_point_id) ?? siteDisplayName(o.site_id);
}

function weightOf(o: OpsOrder): number {
  return o.verified_kg ?? o.estimated_kg;
}

// ── Outlet pickup orders (OrderSchema) ─────────────────────────────────────

export function toOrderSchema(o: OpsOrder): OrderSchema {
  const site = DEMO_SITES.find((s) => s.id === o.site_id);
  return {
    id: o.id,
    waste_type: wasteTypeOf(o.material_id),
    amount: Math.round(weightOf(o) * 10) / 10,
    amount_unit: PickupOrderAmountUnit.KG,
    organization_id: o.organization_id,
    data: {
      reference: o.reference,
      pickup_address: o.site_id ?? '',
      container_id: o.container_id ?? '',
      fill_level: o.fill_level ?? '',
      priority: o.priority,
      notes: o.notes,
      special_instructions: o.special_instructions,
      estimated_weight_kg: o.estimated_kg,
      verified_weight_kg: o.verified_kg,
      scheduled_at: o.scheduled_at,
      picked_up_at: o.picked_up_at,
      completed_at: o.completed_at,
      cancelled_at: o.cancelled_at,
      cancellation_reason: o.cancellation_reason,
      approval_status: o.approval_status,
      truck_id: o.truck_id,
      truck_name: truckName(o.truck_id),
      driver_id: o.driver_id,
      driver_name: driverName(o.driver_id),
      facility_id: o.facility_id,
      facility_name: facilityDisplayName(o.facility_id),
      collection_point_id: o.collection_point_id,
      route_run_id: o.route_run_id,
    },
    material_type_id: o.material_id,
    material_type: toMaterialType(o.material_id, o.organization_id),
    status: o.status,
    created_at: o.created_at,
    updated_at: o.updated_at,
    sale_amount: o.sale_amount ?? 0,
    collection_site_id: o.site_id ?? undefined,
    collection_site: o.site_id
      ? { id: o.site_id, data: { name: siteDisplayName(o.site_id) ?? site?.name_en ?? '', code: site?.code } }
      : undefined,
  };
}

// ── Approval / collection rows ─────────────────────────────────────────────

export function toApprovalRow(o: OpsOrder): ApprovalOrderRow {
  return {
    pickup_order_id: o.id,
    status: o.status,
    approval_status: o.approval_status,
    point_name: pointName(o),
    supplier_name: supplierDisplayName(o.supplier_id),
    waste_type: materialName(o.material_id),
    estimated_weight: o.estimated_kg,
    verified_weight_kg: o.verified_kg,
    sale_amount: o.sale_amount,
    flags: [...o.flags],
    pickup_approval_status: o.pickup_approval?.status ?? null,
    pickup_approval_name: o.pickup_approval?.approver_name ?? null,
    completed_at: o.completed_at ?? o.picked_up_at ?? o.cancelled_at,
    route_run_id: o.route_run_id,
    rejection_reason: o.rejection_reason ? tr(o.rejection_reason, o.rejection_reason_ar) : null,
    rejected_at: o.rejected_at,
    handoff: null,
  };
}

const RECYCLER_FACILITY_IDS = new Set(DEMO_FACILITIES.map((f) => f.id));

/** Orders delivered into the organization's facilities, by verification state. */
export function pendingApprovalRows(orgId: string, approvalStatus: string, runDate?: string): ApprovalOrderRow[] {
  return allOrders()
    .filter((o) => o.recycler_org_id === orgId && o.status === 'completed' && o.facility_id && RECYCLER_FACILITY_IDS.has(o.facility_id))
    .filter((o) => (o.approval_status ?? 'pending') === approvalStatus)
    .filter((o) => !runDate || orderDay(o) === runDate)
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
    .map(toApprovalRow);
}

/** Every route-run collection (done, in transit and cancelled), optionally for one day. */
export function collectionRows(orgId: string, runDate?: string): ApprovalOrderRow[] {
  return allOrders()
    .filter((o) => o.recycler_org_id === orgId && o.source === 'route_run')
    .filter((o) => !runDate || orderDay(o) === runDate)
    .sort((a, b) => (b.picked_up_at ?? b.cancelled_at ?? '').localeCompare(a.picked_up_at ?? a.cancelled_at ?? ''))
    .map(toApprovalRow);
}

// ── Route runs (Trips) ─────────────────────────────────────────────────────

function facilitySnapshot(id: string | null) {
  const f = facilityRecordById(id);
  if (!f) return null;
  return { id: f.id, name: facilityDisplayName(f.id), type: f.type, latitude: f.latitude, longitude: f.longitude, address: f.address };
}

function runStatus(run: OpsRun): string {
  return run.status;
}

export function toRouteRun(run: OpsRun): OpsRouteRun {
  const route = routeRecordById(run.route_id);
  const counts: Record<string, number> = {};
  const stops: OpsRouteRunStop[] = run.stops.map((s) => {
    counts[s.status] = (counts[s.status] ?? 0) + 1;
    const order = s.pickup_order_id ? allOrders().find((o) => o.id === s.pickup_order_id) : undefined;
    const point = pointRecordById(s.collection_point_id);
    const supplierName = supplierDisplayName(s.supplier_id);
    return {
      id: s.id,
      collection_point_id: s.collection_point_id,
      supplier_id: s.supplier_id,
      sequence: s.sequence,
      status: s.status,
      pickup_order_id: s.pickup_order_id,
      arrived_at: s.arrived_at,
      completed_at: s.completed_at,
      cancellation_reason: s.cancellation_reason,
      cancellation_notes: s.cancellation_notes,
      cancelled_at: s.cancelled_at,
      point_snapshot: {
        id: s.collection_point_id,
        name: pointDisplayName(s.collection_point_id),
        supplier_id: s.supplier_id,
        supplier_name: supplierName,
        city: point?.city ?? null,
        address: point?.address ?? null,
        latitude: point?.latitude ?? null,
        longitude: point?.longitude ?? null,
        materials: [{ id: s.material_id, name: materialName(s.material_id) }],
      },
      weight_kg: order && order.status !== 'cancelled' ? weightOf(order) : null,
      price_amount: order ? order.sale_amount : null,
      order_status: order?.status ?? null,
      order_approval_status: order?.approval_status ?? null,
      pickup_approval_status: order?.pickup_approval?.status ?? null,
      rejection_reason: order?.rejection_reason ? tr(order.rejection_reason, order.rejection_reason_ar) : null,
      handed_off: false,
    };
  });
  const facility = facilitySnapshot(run.facility_id);
  return {
    id: run.id,
    organization_id: DEMO_RECYCLER_ORG_ID,
    route_id: run.route_id,
    route_name: routeDisplayName(run.route_id),
    truck_id: run.truck_id,
    truck_name: truckName(run.truck_id),
    driver_id: run.driver_id,
    driver_name: driverName(run.driver_id),
    run_date: run.run_date,
    status: runStatus(run),
    clock_in_at: run.clock_in_at,
    clock_out_at: run.clock_out_at,
    offload_at: run.offload_at,
    planned_geometry: { type: 'LineString', coordinates: run.geometry.map(([lng, lat]) => [lng, lat]) },
    planned_distance_km: run.distance_km,
    planned_duration_seconds: run.duration_seconds,
    route_snapshot: {
      id: run.route_id,
      name: routeDisplayName(run.route_id),
      city: route?.city ?? null,
      stop_count: run.stops.length,
      start_facility: facility,
      end_facility: facility,
    },
    stop_status_counts: counts,
    stops,
  };
}

export function runsInRange(orgId: string, from?: string, to?: string): OpsRouteRun[] {
  if (orgId !== DEMO_RECYCLER_ORG_ID) return [];
  return allRuns()
    .filter((r) => (!from || r.run_date >= from) && (!to || r.run_date <= to))
    .sort((a, b) => a.run_date.localeCompare(b.run_date) || (a.clock_in_at ?? a.scheduled_start_at).localeCompare(b.clock_in_at ?? b.scheduled_start_at))
    .map(toRouteRun);
}

// ── Driver requests (fleet assignments) ────────────────────────────────────

const REQUEST_EXPIRY_MS = 6 * HOUR_MS;

function truckCity(truck: { id: string; latitude?: number | null }): string {
  // Story trucks know their city; trucks added in the session are placed by where they are parked.
  return DEMO_TRUCKS.find((t) => t.id === truck.id)?.city ?? ((truck.latitude ?? 24.7) < 23 ? 'Jeddah' : 'Riyadh');
}

/** Drivers dispatch can offer a request to right now: the live truck/driver pairing, not the seed. */
function availableDriversFor(city: string): Array<{ driverId: string; truckId: string }> {
  return listTrucks(DEMO_RECYCLER_ORG_ID)
    .filter((t) => truckCity(t) === city && t.driver_id && t.status !== 'maintenance')
    .map((t) => ({ driverId: t.driver_id as string, truckId: t.id }));
}

function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return round1(2 * 6371 * Math.asin(Math.sqrt(h)) * 1.3);
}

function assignmentRow(o: OpsOrder, attempt: number, status: DriverRequestStatus, driverId: string, truckId: string, assignedAt: string, respondedAt: string | null): FleetDriverRequestMonitor {
  const site = DEMO_SITES.find((s) => s.id === o.site_id);
  const truck = DEMO_TRUCKS.find((t) => t.id === truckId);
  const liveTruck = truckRecordById(truckId);
  const facility = DEMO_FACILITIES.find((f) => f.id === (o.facility_id ?? (site?.city === 'Jeddah' ? DEMO_FACILITIES[1].id : DEMO_FACILITIES[0].id)));
  const truckFacility = DEMO_FACILITIES.find((f) => f.id === truck?.facility_id) ?? DEMO_FACILITIES[0];
  return {
    id: demoId('a6', (o.seq * 4 + attempt) & 0xffffff),
    pickup_order_id: o.id,
    status,
    assigned_at: assignedAt,
    responded_at: respondedAt,
    expires_at: iso(new Date(Date.parse(assignedAt) + REQUEST_EXPIRY_MS)),
    distance_km: site ? distanceKm(truckFacility, site) : null,
    driver_id: driverId,
    driver_name: driverName(driverId) ?? driverId,
    truck_id: truckId,
    truck_name: liveTruck?.name ?? truck?.plate_number ?? truckId,
    waste_outlet_id: o.outlet_org_id ?? o.organization_id,
    waste_outlet_name: pointName(o) ?? organizationName(o.organization_id) ?? '',
    destination_id: facility?.id ?? null,
    destination_name: facility ? facilityDisplayName(facility.id) : null,
    waste_type: wasteTypeOf(o.material_id),
    material_name: materialName(o.material_id),
    amount: weightOf(o),
    amount_unit: 'kg',
    pickup_status: o.status,
  };
}

/**
 * Dispatch history of an on-demand order, derived from its state: accepted
 * orders show who took it (sometimes after a decline), fresh pending orders
 * wait on a live offer, stale pending orders show offers that lapsed.
 */
function requestsFor(o: OpsOrder): FleetDriverRequestMonitor[] {
  const site = DEMO_SITES.find((s) => s.id === o.site_id);
  const pool = availableDriversFor(site?.city ?? 'Riyadh');
  if (!pool.length) return [];
  const key = `req:${o.id}`;
  const now = Date.now();
  const created = Date.parse(o.created_at);
  const pickOther = (exclude: string | null, salt: string) => {
    const others = pool.filter((p) => p.driverId !== exclude);
    return (others.length ? others : pool)[hashString(key + salt) % (others.length || pool.length)];
  };
  const out: FleetDriverRequestMonitor[] = [];
  if ((o.status === 'in_transit' || o.status === 'completed') && o.driver_id && o.truck_id) {
    const acceptedAt = Math.min(Date.parse(o.picked_up_at ?? o.scheduled_at) - 2.5 * HOUR_MS, now - 10 * 60000);
    const assigned = Math.max(created + 5 * 60000, acceptedAt - (5 + unit(key + 'r') * 20) * 60000);
    if (unit(key + 'd') < 0.22) {
      const other = pickOther(o.driver_id, 'd');
      const declinedAt = Math.max(created + 2 * 60000, assigned - 40 * 60000);
      out.push(assignmentRow(o, 0, 'rejected', other.driverId, other.truckId, iso(new Date(declinedAt - 9 * 60000)), iso(new Date(declinedAt))));
    }
    out.push(assignmentRow(o, 1, 'accepted', o.driver_id, o.truck_id, iso(new Date(assigned)), iso(new Date(acceptedAt))));
    return out;
  }
  if (o.status === 'pending') {
    const age = now - created;
    const first = pickOther(null, 'a');
    if (age < 3 * DAY_MS) {
      if (unit(key + 'd') < 0.3 && age > HOUR_MS) {
        const declined = created + 12 * 60000;
        out.push(assignmentRow(o, 0, 'rejected', first.driverId, first.truckId, iso(new Date(created + 4 * 60000)), iso(new Date(declined))));
        const second = pickOther(first.driverId, 'b');
        const offered = Math.max(declined + 3 * 60000, now - (10 + unit(key + 'o') * 240) * 60000);
        out.push(assignmentRow(o, 1, 'pending', second.driverId, second.truckId, iso(new Date(offered)), null));
      } else {
        const offered = Math.max(created + 3 * 60000, now - (5 + unit(key + 'o') * 300) * 60000);
        out.push(assignmentRow(o, 1, 'pending', first.driverId, first.truckId, iso(new Date(Math.min(offered, now - 60000))), null));
      }
      return out;
    }
    // Older pending orders: the offers lapsed or were declined; dispatch will re-offer.
    out.push(assignmentRow(o, 0, unit(key + 'e') < 0.6 ? 'expired' : 'rejected', first.driverId, first.truckId, iso(new Date(created + 6 * 60000)), unit(key + 'e') < 0.6 ? null : iso(new Date(created + 47 * 60000))));
    if (unit(key + 'e2') < 0.5) {
      const second = pickOther(first.driverId, 'c');
      out.push(assignmentRow(o, 1, 'expired', second.driverId, second.truckId, iso(new Date(created + 7 * HOUR_MS)), null));
    }
    return out;
  }
  if (o.status === 'cancelled' && unit(key + 'k') < 0.55) {
    const at = o.cancelled_at ?? o.updated_at;
    const offered = pickFirst(pool, key);
    out.push(assignmentRow(o, 0, 'cancelled', offered.driverId, offered.truckId, iso(new Date(Math.max(created + 5 * 60000, Date.parse(at) - 2 * HOUR_MS))), at));
  }
  return out;
}

function pickFirst(pool: Array<{ driverId: string; truckId: string }>, key: string) {
  return pool[hashString(key + 'f') % pool.length];
}

export function fleetAssignments(orgId: string, status: string | undefined, page: unknown, limit: unknown) {
  const all = orgId === DEMO_RECYCLER_ORG_ID
    ? allOrders()
        .filter((o) => o.source === 'on_demand')
        .flatMap(requestsFor)
        .sort((a, b) => b.assigned_at.localeCompare(a.assigned_at))
    : [];
  const status_counts: Partial<Record<DriverRequestStatus, number>> = {};
  all.forEach((a) => {
    status_counts[a.status] = (status_counts[a.status] ?? 0) + 1;
  });
  const filtered = status ? all.filter((a) => a.status === status) : all;
  const p = Math.max(1, Number(page) || 1);
  const l = Math.max(1, Number(limit) || 20);
  return {
    assignments: filtered.slice((p - 1) * l, p * l),
    total: filtered.length,
    page: p,
    limit: l,
    status_counts,
  };
}

// ── Recycler pre-orders (purchase requests) ────────────────────────────────

interface PreOrderSeed {
  id: string;
  material_id: string;
  created_at: string;
  window_end: string;
  amount_kg: number;
  cancelled: boolean;
}

let preOrders: PreOrderSeed[] = [];

const SECOND_STREAMS = ['pet', 'mixed_paper', 'ldpe_film', 'aluminium_cans', 'uco', 'hdpe', 'steel_scrap', 'pet', 'mixed_paper', 'wood_pallets', 'office_paper', 'ldpe_film'];

function linkedOrders(p: PreOrderSeed): OpsOrder[] {
  return allOrders().filter(
    (o) =>
      o.source === 'on_demand' &&
      o.material_id === p.material_id &&
      (o.status === 'in_transit' || o.status === 'completed') &&
      o.created_at >= p.created_at &&
      o.created_at < p.window_end,
  );
}

function fulfilledKg(list: OpsOrder[]): number {
  return list
    .filter((o) => o.status === 'completed' && o.approval_status !== 'rejected')
    .reduce((sum, o) => sum + (o.verified_kg ?? 0), 0);
}

/** 24 pre-orders: OCC every month plus a rotating second stream, sized from the pickups that filled them. */
export function resetPreOrders(): void {
  preOrders = [];
  let n = 0;
  for (let offset = -11; offset <= 0; offset += 1) {
    const start = monthStart(offset);
    const created = iso(new Date(start.getTime() + 2 * DAY_MS + 9 * HOUR_MS));
    const end = iso(monthStart(offset + 1));
    [DEMO_MATERIAL_IDS.occ, DEMO_MATERIAL_IDS[SECOND_STREAMS[offset + 11]]].forEach((materialId, k) => {
      n += 1;
      const seed: PreOrderSeed = { id: ids.preOrder(n), material_id: materialId, created_at: created, window_end: end, amount_kg: 0, cancelled: false };
      const linked = linkedOrders(seed);
      const done = fulfilledKg(linked);
      const expected = linked.reduce((s, o) => s + o.estimated_kg, 0);
      const key = `pre:${n}`;
      const step = Math.max(done, expected) < 2000 ? 100 : 500;
      if (offset === -7 && k === 1) seed.cancelled = true;
      if (offset === 0) {
        // This month's request is still filling: sized on everything raised so far.
        seed.amount_kg = Math.max(500, Math.ceil((expected * 1.05) / step) * step);
      } else if (done === 0) {
        seed.amount_kg = Math.round(DEMO_APPETITE_LINES.find((a) => a.material_id === materialId)?.target_kg ?? 2000);
      } else if (unit(key) < 0.7) {
        // Closed out: the pickups covered the order (slightly over, as weighed loads do).
        seed.amount_kg = Math.max(step, Math.floor(done / step) * step);
      } else {
        seed.amount_kg = Math.ceil((done * 1.25) / step) * step;
      }
      preOrders.push(seed);
    });
  }
}

function linkedPickup(o: OpsOrder): LinkedPickupMonitor {
  const phase =
    o.status === 'in_transit'
      ? 'in_transit'
      : o.status === 'completed'
        ? o.approval_status === 'approved' ? 'verified' : 'delivered'
        : o.status === 'pending' ? 'assigned' : null;
  return {
    pickup_order_id: o.id,
    status: o.status,
    amount: o.estimated_kg,
    amount_unit: 'kg',
    verified_weight_kg: o.verified_kg,
    waste_outlet_id: o.outlet_org_id,
    waste_outlet_name: pointName(o) ?? organizationName(o.outlet_org_id),
    delivery_phase: phase,
    created_at: o.created_at,
  };
}

function toPreOrder(p: PreOrderSeed): RecyclerPreOrderMonitor {
  const m = materialById(p.material_id);
  const category = m ? categoryByCode(m.category_code) : undefined;
  const linked = linkedOrders(p).sort((a, b) => b.created_at.localeCompare(a.created_at));
  const fulfilled = Math.round(fulfilledKg(linked) * 10) / 10;
  const percent = p.amount_kg > 0 ? Math.round((fulfilled / p.amount_kg) * 1000) / 10 : 0;
  const status = p.cancelled ? 'cancelled' : fulfilled >= p.amount_kg ? 'completed' : fulfilled > 0 ? 'partially_fulfilled' : 'pending';
  const lastActivity = linked.reduce((acc, o) => (o.updated_at > acc ? o.updated_at : acc), p.created_at);
  return {
    id: p.id,
    material_category: m?.category_code ?? 'mixed',
    material_name_snapshot: m ? tr(m.name_en, m.name_ar) : null,
    category_name_snapshot: category ? tr(category.name_en, category.name_ar) : null,
    regulatory_code_snapshot: m?.ewc_codes[0] ?? null,
    hazard_status_snapshot: m?.hazardous ? 'hazardous' : 'non_hazardous',
    physical_state_snapshot: m?.default_physical_state ?? null,
    amount: p.amount_kg,
    amount_unit: 'kg',
    amount_kg: p.amount_kg,
    status,
    created_at: p.created_at,
    updated_at: lastActivity,
    fulfilled_kg: fulfilled,
    remaining_kg: Math.max(0, Math.round((p.amount_kg - fulfilled) * 10) / 10),
    fulfillment_percent: percent,
    linked_pickups: p.cancelled ? [] : linked.map(linkedPickup),
  };
}

export function preOrdersMonitor(orgId: string, offset: unknown, limit: unknown) {
  const all = orgId === DEMO_RECYCLER_ORG_ID ? preOrders.map(toPreOrder).sort((a, b) => b.created_at.localeCompare(a.created_at)) : [];
  const overview: RecyclerOrderOverview = {
    total_orders: all.length,
    total_ordered_kg: all.reduce((s, p) => s + p.amount_kg, 0),
    fulfilled_kg: Math.round(all.reduce((s, p) => s + p.fulfilled_kg, 0) * 10) / 10,
    remaining_kg: Math.round(all.filter((p) => p.status !== 'cancelled').reduce((s, p) => s + p.remaining_kg, 0) * 10) / 10,
    pending_count: all.filter((p) => p.status === 'pending').length,
    partially_fulfilled_count: all.filter((p) => p.status === 'partially_fulfilled').length,
    completed_count: all.filter((p) => p.status === 'completed').length,
    cancelled_count: all.filter((p) => p.status === 'cancelled').length,
  };
  const o = Math.max(0, Number(offset) || 0);
  const l = Math.max(1, Number(limit) || 20);
  return {
    organization_id: orgId,
    total_count: all.length,
    pre_orders: all.slice(o, o + l),
    pre_orders_truncated: false,
    overview,
  };
}

// ── Facility intake ────────────────────────────────────────────────────────

export function facilityIncoming(facilityId: string, page: unknown, limit: unknown) {
  const rows: FacilityIncomingOrder[] = allOrders()
    .filter((o) => o.facility_id === facilityId && (o.status === 'completed' || o.status === 'in_transit'))
    .sort((a, b) => (b.completed_at ?? b.picked_up_at ?? '').localeCompare(a.completed_at ?? a.picked_up_at ?? ''))
    .map((o) => ({
      id: o.id,
      route_id: o.route_id ?? '',
      route_name: o.route_id ? routeDisplayName(o.route_id) ?? '' : tr('On-demand pickup', 'طلب استلام عند الطلب'),
      truck_id: o.truck_id ?? '',
      truck_name: truckName(o.truck_id) ?? '',
      driver_id: o.driver_id,
      driver_name: driverName(o.driver_id),
      material_name: materialName(o.material_id),
      waste_type: wasteTypeOf(o.material_id),
      amount: weightOf(o),
      amount_unit: 'kg',
      status: o.status === 'completed' ? (o.approval_status === 'approved' ? 'verified' : o.approval_status === 'rejected' ? 'rejected' : 'delivered') : 'in_transit',
      created_at: o.created_at,
      updated_at: o.updated_at,
    }));
  const p = Math.max(1, Number(page) || 1);
  const l = Math.max(1, Number(limit) || 20);
  return { orders: rows.slice((p - 1) * l, p * l), total: rows.length, page: p, limit: l };
}

// ── Driver history ─────────────────────────────────────────────────────────

export function driverHistory(driverId: string): DriverHistoricalOrder[] {
  return allOrders()
    .filter((o) => o.driver_id === driverId && o.status === 'completed')
    .sort((a, b) => (b.completed_at ?? '').localeCompare(a.completed_at ?? ''))
    .slice(0, 40)
    .map((o) => ({
      delivery_id: ids.delivery(o.seq),
      delivery_name: `${pointName(o) ?? o.reference} → ${facilityDisplayName(o.facility_id) ?? ''}`,
      pickup_order_id: o.id,
      completed_at: o.completed_at,
      waste_type: wasteTypeOf(o.material_id),
      amount: weightOf(o),
      recycler_name: organizationName(o.recycler_org_id),
    }));
}

// ── Delivery evidence ──────────────────────────────────────────────────────

function photo(o: OpsOrder, slot: string, title: string, subtitle: string, hue: number): DeliveryEvidenceFile {
  return {
    asset_id: ids.upload(((o.seq % 0x3ffff) * 16 + (hashString(slot) % 16)) & 0xffffff),
    filename: `${o.reference}-${slot}.svg`,
    file_type: 'image/svg+xml',
    download_url: placeholderPhoto(title, subtitle, hue),
  };
}

function pickupLocation(o: OpsOrder): { lat: number; lng: number; name: string; id: string } {
  const point = pointRecordById(o.collection_point_id);
  const site = DEMO_SITES.find((s) => s.id === o.site_id);
  return {
    lat: point?.latitude ?? site?.lat ?? 24.7136,
    lng: point?.longitude ?? site?.lng ?? 46.6753,
    name: pointName(o) ?? organizationName(o.organization_id) ?? '',
    id: o.outlet_org_id ?? o.supplier_id ?? o.organization_id,
  };
}

/** GET /delivery/pickup-order/{id}: the delivery behind a dispatched order, or null if none yet. */
export function deliveryDetails(o: OpsOrder): DeliveryDetails | null {
  // A delivery exists once a driver took the order (route stops always have one, even if cancelled on site).
  const dispatched = o.status === 'completed' || o.status === 'in_transit' || (o.status === 'cancelled' && o.source === 'route_run');
  if (!dispatched) return null;
  const deliveryId = ids.delivery(o.seq);
  const material = materialName(o.material_id);
  const where = pickupLocation(o);
  const facility = facilityRecordById(o.facility_id);
  const truck = truckRecordById(o.truck_id);
  const driver = DEMO_DRIVERS.find((d) => d.id === o.driver_id);
  const hue = hashString(o.material_id) % 360;
  const before = [photo(o, 'before', tr('Before pickup', 'قبل الاستلام'), material, hue)];
  const after = [photo(o, 'after', tr('After loading', 'بعد التحميل'), where.name.slice(0, 34), (hue + 30) % 360)];
  const recycler = o.status === 'completed' ? [photo(o, 'tipping', tr('Tipping floor', 'منطقة التفريغ'), facilityDisplayName(o.facility_id) ?? '', (hue + 60) % 360)] : [];
  const invoicePhoto = o.status === 'completed' ? photo(o, 'ticket', tr('Weighbridge ticket', 'تذكرة الميزان'), o.invoice_serial ?? '', 210) : null;
  const pricePerKg = o.verified_kg && o.sale_amount ? Math.round((o.sale_amount / o.verified_kg) * 100) / 100 : null;
  const provisional = o.verified_kg != null && o.completed_at ? priceLoad(o.supplier_id, o.material_id, o.verified_kg, o.completed_at) : null;

  const events: DeliveryEventEnriched[] = [];
  const push = (phase: string, at: string | null, lat: number | null, lng: number | null, enriched: DeliveryEventEnriched['enriched_data'] = {}, data: Record<string, unknown> = {}) => {
    if (!at) return;
    events.push({
      id: demoId('a7', ((o.seq % 0x1fffff) * 8 + events.length) & 0xffffff),
      delivery_id: deliveryId,
      phase,
      triggered_by: o.driver_id ?? 'system',
      latitude: lat,
      longitude: lng,
      data,
      event_at: at,
      created_at: at,
      enriched_data: enriched,
    });
  };
  if (o.status === 'cancelled') {
    push('assigned', o.scheduled_at, null, null);
    push('arrived_at_pickup', o.cancelled_at, where.lat, where.lng, { before_photos: before });
    push('cancelled', o.cancelled_at, where.lat, where.lng, {}, { reason: o.cancellation_reason });
  }
  const assigned = o.picked_up_at ? iso(new Date(Date.parse(o.picked_up_at) - 2 * HOUR_MS)) : o.created_at;
  if (o.status !== 'cancelled') push('assigned', o.source === 'route_run' ? o.scheduled_at : assigned < o.created_at ? o.created_at : assigned, null, null);
  push('arrived_at_pickup', o.picked_up_at ? iso(new Date(Date.parse(o.picked_up_at) - 14 * 60000)) : null, where.lat, where.lng, { before_photos: before });
  push('picked_up', o.picked_up_at, where.lat, where.lng, {
    after_photos: after,
    flow: {
      answers: {
        fill_level: { value: o.fill_level },
        loading_photos: { files: after },
        ...(o.pickup_approval ? { point_signature: { value: o.pickup_approval.approver_name } } : {}),
      },
    },
  }, { estimated_weight_kg: o.estimated_kg });
  push('in_transit', o.picked_up_at ? iso(new Date(Date.parse(o.picked_up_at) + 4 * 60000)) : null, where.lat, where.lng);
  if (o.status === 'completed') {
    push('arrived_at_facility', o.completed_at ? iso(new Date(Date.parse(o.completed_at) - 18 * 60000)) : null, facility?.latitude ?? null, facility?.longitude ?? null);
    push('delivered', o.completed_at, facility?.latitude ?? null, facility?.longitude ?? null, {
      recycler_photos: recycler,
      ...(invoicePhoto ? { weight_invoice_photo: invoicePhoto } : {}),
    }, { net_weight_kg: o.verified_kg });
    if (o.approval_status === 'approved') push('verified', o.approved_at, facility?.latitude ?? null, facility?.longitude ?? null);
    if (o.approval_status === 'rejected') push('rejected', o.rejected_at, facility?.latitude ?? null, facility?.longitude ?? null);
  }

  const phase = o.status === 'cancelled' ? 'cancelled' : o.status === 'in_transit' ? 'in_transit' : o.approval_status === 'approved' ? 'verified' : o.approval_status === 'rejected' ? 'rejected' : 'delivered';
  return {
    delivery: {
      id: deliveryId,
      pickup_order_id: o.id,
      truck_id: o.truck_id,
      driver_id: o.driver_id ?? '',
      waste_outlet_id: where.id,
      recycler_id: o.recycler_org_id,
      current_phase: phase,
      waste_type: wasteTypeOf(o.material_id),
      amount: o.estimated_kg,
      estimated_weight: o.estimated_kg,
      total_weight: o.verified_kg,
      is_compliant: o.approval_status === 'approved' ? true : o.approval_status === 'rejected' ? false : null,
      pickup_at: o.picked_up_at,
      delivery_at: o.completed_at,
      created_at: o.created_at,
      updated_at: o.updated_at,
    },
    pickup_order: {
      id: o.id,
      status: o.status,
      waste_type: wasteTypeOf(o.material_id),
      amount: o.estimated_kg,
      amount_unit: 'kg',
      organization_id: o.organization_id,
      organization_name: organizationName(o.organization_id),
      sale_amount: o.sale_amount,
      approval_status: o.approval_status,
      approved_at: o.approved_at,
      rejected_at: o.rejected_at,
      rejection_reason: o.rejection_reason ? tr(o.rejection_reason, o.rejection_reason_ar) : null,
      material_type: { id: o.material_id, name: material, unit_of_measure: 'kg' },
      collection_point_name: pointName(o),
      supplier_name: supplierDisplayName(o.supplier_id),
      data: {
        reference: o.reference,
        pickup_address: siteAddress(o.site_id) ?? pointRecordById(o.collection_point_id)?.address ?? '',
        verified_weight_kg: o.verified_kg,
        estimated_weight_kg: o.estimated_kg,
        flags: [...o.flags],
        weight_corrected_by_user_id: o.weight_corrected_by_user_id,
        fill_level: o.fill_level,
        notes: o.notes,
        route_run_id: o.route_run_id,
      },
      files: o.source === 'on_demand' ? [photo(o, 'request', tr('Bin at request', 'الحاوية عند الطلب'), material, (hue + 90) % 360)] : [],
      created_at: o.created_at,
      updated_at: o.updated_at,
    },
    events,
    driver: driver ? { id: driver.id, username: tr(driver.full_name, driver.full_name_ar), phone: driver.phone } : null,
    truck: truck ? { id: truck.id, name: truck.name, status: truck.status } : null,
    waste_outlet: { id: where.id, name: where.name, latitude: where.lat, longitude: where.lng, google_maps_url: mapsUrl(where.lat, where.lng) },
    recycler: facility && facility.latitude != null && facility.longitude != null
      ? { id: facility.id, name: facilityDisplayName(facility.id) ?? '', latitude: facility.latitude, longitude: facility.longitude, google_maps_url: mapsUrl(facility.latitude, facility.longitude) }
      : null,
    weight_invoice: o.status === 'completed' && o.completed_at
      ? {
          id: demoId('a8', o.seq & 0xffffff),
          serial_number: o.invoice_serial ?? '',
          total_weight: o.verified_kg,
          waste_price_per_kg: pricePerKg ?? (provisional && o.verified_kg ? Math.round((provisional.amount / o.verified_kg) * 100) / 100 : null),
          total_price: o.approval_status === 'rejected' ? 0 : o.sale_amount ?? provisional?.amount ?? null,
          notes: o.approval_status === 'rejected' ? tr(o.rejection_reason ?? '', o.rejection_reason_ar) : null,
          photo: invoicePhoto,
          created_at: o.completed_at,
        }
      : null,
    pickup_approval: o.pickup_approval
      ? {
          id: demoId('a9', o.seq & 0xffffff),
          status: o.pickup_approval.status,
          weight_kg: o.pickup_approval.weight_kg,
          approver_name: o.pickup_approval.approver_name,
          rejection_note: o.pickup_approval.rejection_note,
          signature_base64: o.pickup_approval.status === 'approved' ? signatureImage(o.pickup_approval.approver_name) : null,
          responded_at: o.pickup_approval.responded_at,
          created_at: o.pickup_approval.created_at,
        }
      : null,
    handoff: null,
  };
}
